import { PencilIcon, TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import FilteredTable from "../../components/FilteredTable";
import { LoadingMask } from "../../components/LoadingMask";
import { NewUserPath, UpdateUserPath } from "../../constants/Routes";
import { RoleEnum, RoleLabel } from "../../enums/Role";
import { User } from "../../models/User";
import { AddBackupAgentsModal } from "../../components/agents/backups/AddBackupAgentsModal";
import { AppDispatch, RootState } from "../../state/store";
import { RemoveBackupAgentModal } from "../../components/agents/backups/RemoveBackupAgentModal";
import { deleteBackupAgent, deleteUser } from "../../state/users/actions";
import { Toast } from "../../components/Toast";
import { toast, ToastContainer } from "react-toastify";
import { DeleteAgentModal } from "../../components/agents/DeleteAgentModal";

export const Users = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const users = useSelector((state: RootState) => state.users.users);
  const agents = useSelector((state: RootState) => state.users.agents);
  const isLoading = useSelector((state: any) => state.users.isLoading);
  const error = useSelector((state: any) => state.users.error);
  const isBackupLoading = useSelector((state: RootState) => state.users.isBackupLoading);
  const backupError = useSelector((state: any) => state.users.backupError);
  const backupAgents = useSelector((state: RootState) => state.users.backupAgents);
  const [agencyAgents, setAgencyAgents] = useState<User[]>([])
  const [toAddAgents, setToAddAgents] = useState<User[]>([])
  const [showRemoveBackupError, setShowRemoveBackupError] = useState(false);
  const [showUserError, setShowUserError] = useState(false);

  const [addBackupAgentsModal, setAddBackupAgentsModal] = useState<{
    show: boolean;
    agents?: any;
  }>({ show: false, agents: [] });

  const [ showRemoveBackupAgentModal, setShowRemoveBackupAgentModal] = useState<{
    agent?: User;
    show: boolean;
  }>({ show: false, agent: undefined });

  const [ showDeleteAgentModal, setShowDeleteAgentModal] = useState<{
    user?: User;
    show: boolean;
  }>({ show: false, user: undefined });

  useEffect(() => {
    if (showRemoveBackupError) {
        Toast(
            t(backupError?.reason ?? "SOMETHING_WENT_WRONG"),
            t(backupError?.cause_info)
        );
    }

    if (showUserError) {
      Toast(
            t(error?.reason ?? "SOMETHING_WENT_WRONG"),
            t(error?.cause_info)
        );
    }
  }, [showRemoveBackupError, backupError, showUserError, error, t]);

  useEffect(() => {
    if(currentUser?.role_id === RoleEnum.AGENT) {
      const onlyAgents = agents.filter((a) => a.id !== currentUser?.id);
      const displayAgents = onlyAgents.filter((a) => {
        if(a.role_id === RoleEnum.AGENT) {
          const index = backupAgents.findIndex((bA) => bA.id === a.id);
          return index === -1;
        }

        return false;
      })
      setToAddAgents(displayAgents)
      setAgencyAgents(backupAgents)
    } else {
      setToAddAgents([])
      setAgencyAgents(users)
    }
  }, [currentUser, users, backupAgents])

  const usersWithData = useMemo(
    () =>
      agencyAgents.map((item: User) => ({
        ...item,
        fullname: `${item.first_name} ${item.last_name}`,
      })),
    [agencyAgents]
  );

  const updateUser = (row: any) => {
    navigate(UpdateUserPath, { state: { user: row } });
  };

  const removeBackupAgent = (agent: User) => {
    //TODO: remove address from database
    dispatch(deleteBackupAgent(agent)).then((e) => {
      if (e.type === "users/deleteBackupAgent/rejected") {
          setShowRemoveBackupError(true);
      } else {
          toast(t("SUCCESSFULLY_REMOVE_BACKUP_AGENT"));
      }
    });
  }

  const columns = useMemo(
    () => {
      const showColumns = [
        {
          Header: t("NAME"),
          accessor: "fullname",
        },
        {
          Header: t("PHONE"),
          accessor: "phone",
        },
        {
          Header: t("EMAIL"),
          accessor: "email",
        },
        {
          Header: t("ROLE"),
          accessor: "roleId",
          Cell: (row: any) => {
            const { role_id } = row.row.original;
            return RoleLabel(`${role_id}`);
          },
        },
      ];

      if (currentUser?.role_id === RoleEnum.AGENT) {
        return [
          ...showColumns,
          {
            Header: " ",
            Cell: (row: any) => {
              return (
                <div className="flex flex-row justify-center divide-x">
                  <Button
                    isTerciary
                    tooltip={t("REMOVE_BACKUP_AGENT")}
                    iconOnly
                    onClick={() => setShowRemoveBackupAgentModal({ show: true, agent: row.row.original })}
                    classNames="mr-2"
                  >
                    <TrashIcon className="h-5 aspect-square text-red-600" />
                  </Button>
                </div>
              );
            }
          }
        ];
      } else {
        return [
          ...showColumns,
          {
            Header: " ",
            Cell: (row: any) => {
              return (
                <div className="flex flex-row justify-center divide-x">
                  <Button
                    isTerciary
                    tooltip={t("UPDATE_USER")}
                    iconOnly
                    isDisabled={(row.row.original.id === currentUser?.id)}
                    onClick={() => updateUser(row.row.original)}
                    classNames="mr-2"
                  >
                    <PencilIcon className="h-5 aspect-square text-blue" />
                  </Button>
                  <Button
                    isTerciary
                    tooltip={t("DELETE_USER")}
                    iconOnly
                    isDisabled={(row.row.original.role_id !== RoleEnum.AGENT && currentUser?.role_id === RoleEnum.AGENT)
                      || (row.row.original.id === currentUser?.id)}
                    onClick={() => setShowDeleteAgentModal({ show: true, user: row.row.original })}
                    classNames="pl-2"
                  >
                    <TrashIcon className="h-5 aspect-square text-red-600" />
                  </Button>
                </div>
              );
            }
          }
        ];
      }
      
    }, [t, updateUser, removeBackupAgent]
  );

  const onDeleteAgent = (agent: User) => {
    dispatch(deleteUser({ agencyId: currentUser?.agency_id, userId: agent.id!})).then((e) => {
      if (e.type === "users/deleteUser/rejected") {
          setShowUserError(true);
      } else {
          toast(t("SUCCESSFULLY_DELETE_USER"));
      }
    });
  }

  return (
    <>
      {(isLoading || isBackupLoading) && <LoadingMask />}
      <ToastContainer progressStyle={{ background: "#D4AF37" }} />
      <DeleteAgentModal
        info={showDeleteAgentModal}
        onClose={() => setShowDeleteAgentModal({ show: false, user: undefined })}
        deleteUser={(user: User) => onDeleteAgent(user)}
      />
      <FilteredTable
        columns={columns}
        data={usersWithData}
        buttonAction={
          currentUser && currentUser?.role_id !== RoleEnum.AGENT
          ? <Button onClick={() => navigate(NewUserPath)}>
              <span className="flex items-center font-semibold pr-3">
                <div className="w-8 p-1 aspect-square mr-2">
                  <UserPlusIcon />
                </div>
                {t("NEW_USER")}
              </span>
            </Button>
          : currentUser && currentUser?.role_id === RoleEnum.AGENT
            ? <Button onClick={() => setAddBackupAgentsModal({ show: true, agents: toAddAgents })}>
                <span className="flex items-center font-semibold pr-3">
                  <div className="w-8 p-1 aspect-square mr-2">
                    <UserPlusIcon />
                  </div>
                  {t("ADD_BACKUP_AGENT")}
                </span>
              </Button>
            : <div></div>
        }
      />
      <AddBackupAgentsModal
        info={addBackupAgentsModal}
        onClose={() => setAddBackupAgentsModal({ show: false, agents: [] })}
      />
      <RemoveBackupAgentModal
          info={showRemoveBackupAgentModal}
          onClose={() => setShowRemoveBackupAgentModal({show : false, agent: undefined})}
          deleteAgent={(agent: User) => removeBackupAgent(agent)}
        />
    </>
  );
};
